/* eslint @typescript-eslint/no-explicit-any: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import api from "./api";
import { apiSlice } from "./apiSlice";
import UserSerializer from "./serializers/UserSerializer";
import store from "../../app/store";
import { User } from "../../app/types/User";
import { Tokens, TokenManager } from "../../common/utils/TokenManager";
import { WithRequired } from "../../common/utils/types";

export interface SigninData {
    email: string;
    password: string;
}

export interface SignupData {
    email: string;
    username: string;
    password: string;
}

const userSerializer = new UserSerializer();

const userApi = apiSlice.injectEndpoints({
    endpoints: (build) => ({
        getCurrentUser: build.query<User, void>({
            query: () => ({ url: `/auth/users/me/` }),
            transformResponse: (response) => userSerializer.deserialize(response),
        }),
        getUser: build.query<User, number>({
            query: (id) => ({ url: `/auth/users/${id}/` }),
            transformResponse: (response) => userSerializer.deserialize(response),
            providesTags: (result, error, arg) => [{ type: "User", id: arg }],
        }),
        createUser: build.mutation<void, SignupData>({
            query: (data) => ({ url: `/auth/users/`, method: "POST", data }),
        }),
        deleteUser: build.mutation<void, void>({
            query: () => ({ url: `/auth/users/me/`, method: "DELETE" }),
        }),
        updateProfile: build.mutation<void, WithRequired<User, "id">>({
            query: (arg) => {
                const data = {
                    bio: arg.description,
                    education: arg.education,
                    gender: arg.gender,
                    age: arg.age,
                    hobby: arg.hobby,
                };
                return { url: `/NI-api/profile/${arg.id}/`, method: "PATCH", data };
            },
            invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    userApi.util.updateQueryData("getUser", arg.id, (user) => {
                        Object.assign(user, arg);
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetCurrentUserQuery,
    useGetUserQuery,
    useCreateUserMutation,
    useDeleteUserMutation,
    useUpdateProfileMutation,
} = userApi;

// signin / signout
export async function signinUser(data: SigninData, remember = false): Promise<User> {
    store.dispatch(apiSlice.util.resetApiState());
    const tokens = await getTokens(data);
    TokenManager.setToken(tokens, remember);
    return getCurrentUser();
}

export function signoutUser() {
    TokenManager.clearToken();
    store.dispatch(apiSlice.util.resetApiState());
}

async function getTokens(data: SigninData): Promise<Tokens> {
    const response = await api.post("/auth/jwt/create/", data);
    return response.data;
}

export async function getCurrentUser(): Promise<User> {
    const response = await api.get("/auth/users/me/");
    if (!response.data.id) {
        throw new Error("User not found");
    }
    return response.data;
}
